import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Divider, MenuItem, Paper, TextField, Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Timeline as TimelineIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { BuyerOrderService } from '../../services/buyerOrder.service';
import { StyleService } from '../../services/style.service';
import {
    BuyerOrderStatus, IBuyerOrder, IBuyerOrderLineRequest, ICreateBuyerOrderRequest,
} from '../../interfaces/garments/buyerOrder.interface';
import { IBuyer } from '../../interfaces/garments/buyer.interface';
import { IStyle } from '../../interfaces/garments/style.interface';
import { BuyerService } from '../../services/buyer.service';
import { PERMISSIONS } from '../../constants/permissions';
import BuyerAutocomplete from './BuyerAutocomplete';
import CommercialPartyAutocomplete from '../commercial/CommercialPartyAutocomplete';
import BuyerOrderLinesEditor from './BuyerOrderLinesEditor';
import BuyerOrderStatusDialog from './BuyerOrderStatusDialog';
import BuyerOrderAmendDialog from './BuyerOrderAmendDialog';
import MaterialConsumptionEditor from './MaterialConsumptionEditor';
import MaterialRequirementView from './MaterialRequirementView';
import TnaCalendarPanel from './TnaCalendarPanel';
import OrderQcPanel from './OrderQcPanel';
import { AMENDABLE_BUYER_ORDER_STATUSES, EDITABLE_BUYER_ORDER_STATUSES, legalBuyerOrderStatusTargets } from './buyerOrderTransitions';
import { enumLabel } from '../../utils/garmentsLabels';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';

// Mirrors `CreateBuyerOrderRequest`'s @Size bounds exactly (backend/.../merch/dataclass/CreateBuyerOrderRequest.java).
const MAX = {
    orderNumber: 60, buyerPoNumber: 120, destinationCountry: 120, destinationPort: 120,
    incoterm: 20, currencyCode: 3, remarks: 1000,
};

const STATUS_COLORS: Record<BuyerOrderStatus, 'default' | 'info' | 'primary' | 'success' | 'warning' | 'error'> = {
    DRAFT: 'default',
    CONFIRMED: 'info',
    IN_PRODUCTION: 'primary',
    SHIPPED: 'success',
    CLOSED: 'default',
    CANCELLED: 'error',
};

interface FormState {
    buyer: IBuyer | null;
    orderNumber: string;
    buyerPoNumber: string;
    orderDate: string;
    shipDate: string;
    destinationCountry: string;
    destinationPort: string;
    incoterm: string;
    currencyCode: string;
    remarks: string;
    agentPartyId: string | null;
    agentCommissionPercent: string;
}

const emptyForm: FormState = {
    buyer: null, orderNumber: '', buyerPoNumber: '', orderDate: toLocalIsoDate(),
    shipDate: '', destinationCountry: '', destinationPort: '', incoterm: '', currencyCode: '', remarks: '',
    agentPartyId: null, agentCommissionPercent: '',
};

/**
 * Create/edit screen for a buyer order (garments-merchandising T10) — hosts the header fields plus
 * the line-item grid (`BuyerOrderLinesEditor`). New/edit route: `/merchandising/orders/new` and
 * `/merchandising/orders/:id`. Read-only once the order leaves DRAFT (H4/REV 2) — only the
 * Change Status and Amend actions remain available; the header/lines grid explain why they are
 * disabled rather than silently doing nothing.
 */
export function BuyerOrderForm() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const toast = useToast();
    const canManage = hasPermission(PERMISSIONS.BUYER_ORDER_MANAGE);
    const isEdit = Boolean(id) && id !== 'new';

    const [order, setOrder] = useState<IBuyerOrder | null>(null);
    const [styleLookup, setStyleLookup] = useState<Record<string, IStyle>>({});
    const [form, setForm] = useState<FormState>(emptyForm);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [linesSaving, setLinesSaving] = useState(false);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [amendDialogOpen, setAmendDialogOpen] = useState(false);

    const institutionId = AuthService.getSelectedInstitution() ?? '';

    const resolveStyles = useCallback(async (institutionId: string, o: IBuyerOrder) => {
        const uniqueIds = Array.from(new Set(o.lines.map(l => l.styleId)));
        if (uniqueIds.length === 0) return;
        try {
            const resolved = await Promise.all(uniqueIds.map(sid => StyleService.getById(institutionId, sid)));
            setStyleLookup(prev => {
                const next = { ...prev };
                resolved.forEach(s => { next[s.id] = s; });
                return next;
            });
        } catch (e) {
            toast.showApiError(e, 'Failed to load line styles');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId || !id || !isEdit) return;
        try {
            setLoading(true);
            const res = await BuyerOrderService.getById(institutionId, id);
            setOrder(res);
            const buyer = await BuyerService.getById(institutionId, res.buyerId);
            setForm(prev => ({ ...prev, buyer }));
            await resolveStyles(institutionId, res);
        } catch (e) {
            toast.showApiError(e, 'Failed to load order');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEdit]);

    // `toast`/`resolveStyles` are deliberately excluded from `load`'s own deps above and from this
    // effect's deps below — `toast` is a fresh object on every render (from context, and even more
    // so from a test double), and depending on it here would refetch the order on every unrelated
    // re-render instead of only when the route's `id` changes.
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEdit]);

    useEffect(() => {
        if (!order) return;
        setForm(prev => ({
            ...prev,
            orderNumber: order.orderNumber,
            buyerPoNumber: order.buyerPoNumber ?? '',
            orderDate: order.orderDate,
            shipDate: order.shipDate,
            destinationCountry: order.destinationCountry ?? '',
            destinationPort: order.destinationPort ?? '',
            incoterm: order.incoterm ?? '',
            currencyCode: order.currencyCode ?? '',
            remarks: order.remarks ?? '',
            agentPartyId: order.agentPartyId ?? null,
            agentCommissionPercent: order.agentCommissionPercent != null ? String(order.agentCommissionPercent) : '',
        }));
    }, [order]);

    const setField = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm(prev => ({ ...prev, [field]: e.target.value }));

    // Header/lines are only writable while DRAFT — every other status routes changes through
    // Change Status / Amend instead (H4/REV 2).
    const headerLocked = isEdit && order != null && !EDITABLE_BUYER_ORDER_STATUSES.includes(order.status);
    const canEditHeader = canManage && !headerLocked;

    const validate = (): Record<string, string> => {
        const next: Record<string, string> = {};
        if (!form.buyer) next.buyer = 'Buyer is required';
        if (!form.orderNumber.trim()) next.orderNumber = 'Order number is required';
        if (!form.orderDate) next.orderDate = 'Order date is required';
        if (!form.shipDate) next.shipDate = 'Ship date is required';
        if (form.agentCommissionPercent.trim()) {
            const pct = Number(form.agentCommissionPercent);
            if (Number.isNaN(pct) || pct < 0 || pct > 100) {
                next.agentCommissionPercent = 'Commission must be between 0 and 100';
            }
        }
        return next;
    };

    const handleSave = async () => {
        const validationErrors = validate();
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) return;

        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId || !form.buyer) return;

        const request: ICreateBuyerOrderRequest = {
            buyerId: form.buyer.id,
            orderNumber: form.orderNumber.trim(),
            buyerPoNumber: form.buyerPoNumber.trim() || null,
            orderDate: form.orderDate,
            shipDate: form.shipDate,
            destinationCountry: form.destinationCountry.trim() || null,
            destinationPort: form.destinationPort.trim() || null,
            incoterm: form.incoterm.trim() || null,
            currencyCode: form.currencyCode.trim() || null,
            remarks: form.remarks.trim() || null,
            merchandiserUserId: order?.merchandiserUserId ?? null,
            agentCommissionPercent: form.agentCommissionPercent.trim() ? Number(form.agentCommissionPercent) : null,
            // Fix round 1: always send the current agentPartyId -- the backend now only
            // re-validates it when it actually changed from the persisted value (mirrors the
            // buyerId only-on-change precedent), so an unchanged value never 400s even if the
            // referenced agent has since been deactivated.
            agentPartyId: form.agentPartyId,
        };

        setSaving(true);
        try {
            if (isEdit && order) {
                const updated = await BuyerOrderService.update(institutionId, order.id, request);
                setOrder(updated);
                toast.showSuccess('Order updated');
            } else {
                const created = await BuyerOrderService.create(institutionId, request);
                toast.showSuccess('Order created');
                navigate(`/merchandising/orders/${created.id}`, { replace: true });
            }
        } catch (e) {
            toast.showApiError(e, isEdit ? 'Failed to update order' : 'Failed to create order');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveLines = async (requests: IBuyerOrderLineRequest[], usedStyles: IStyle[]) => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId || !order) return;
        setLinesSaving(true);
        try {
            const updated = await BuyerOrderService.replaceLines(institutionId, order.id, requests);
            setOrder(updated);
            setStyleLookup(prev => {
                const next = { ...prev };
                usedStyles.forEach(s => { next[s.id] = s; });
                return next;
            });
            toast.showSuccess('Lines saved');
        } catch (e) {
            toast.showApiError(e, 'Failed to save lines');
        } finally {
            setLinesSaving(false);
        }
    };

    const handleStatusUpdated = (updated: IBuyerOrder) => {
        setOrder(updated);
    };

    const handleAmended = (updated: IBuyerOrder) => {
        setOrder(updated);
    };

    if (loading) {
        return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
    }

    const canChangeStatus = canManage && order != null && legalBuyerOrderStatusTargets(order.status).length > 0;
    const canAmend = canManage && order != null && AMENDABLE_BUYER_ORDER_STATUSES.includes(order.status);

    return (
        <Box sx={{ p: 3 }} data-help-page="merchandising.orderForm.page">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Button data-help="merchandising.orderForm.back" startIcon={<ArrowBackIcon />} onClick={() => navigate('/merchandising/orders')}>
                    Back
                </Button>
                <Typography variant="h5" sx={{ flexGrow: 1 }}>
                    {isEdit ? `Edit Order${order ? `: ${order.orderNumber}` : ''}` : 'New Order'}
                </Typography>
                {order && (
                    <>
                        <Chip data-help="merchandising.orderForm.status" label={enumLabel(order.status)} color={STATUS_COLORS[order.status]} size="small" />
                        {isEdit && (
                            <Button
                                data-help="merchandising.orderForm.journey"
                                size="small"
                                variant="contained"
                                startIcon={<TimelineIcon />}
                                onClick={() => navigate(`/merchandising/orders/${order.id}/journey`)}
                            >
                                Order thread
                            </Button>
                        )}
                        {canChangeStatus && (
                            <Button data-help="merchandising.orderForm.changeStatus" size="small" onClick={() => setStatusDialogOpen(true)}>
                                Change Status
                            </Button>
                        )}
                        {canAmend && (
                            <Button data-help="merchandising.orderForm.amend" size="small" onClick={() => setAmendDialogOpen(true)}>
                                Amend
                            </Button>
                        )}
                    </>
                )}
            </Box>

            {headerLocked && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        This order is {enumLabel(order?.status)}. Its details and lines can only be changed while it is in Draft.
                        {canAmend && ' Use Amend to change the ship date or remarks.'}
                    </Typography>
                </Box>
            )}

            <Paper variant="outlined" sx={{ p: 3, mb: 3 }} data-help-section="merchandising.orderForm.header">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <BuyerAutocomplete
                        data-help="merchandising.orderForm.field.buyer"
                        value={form.buyer}
                        onChange={buyer => setForm(prev => ({ ...prev, buyer }))}
                        // L3 fix: always activeOnly, on create AND edit. BuyerAutocomplete's own
                        // `withSelected` keeps the CURRENT buyer visible/selectable even when it
                        // is inactive (so an existing order's picker still shows its own buyer) —
                        // but the picker must never OFFER a different inactive buyer as a new
                        // choice, since BuyerOrderService.assertBuyerInInstitution rejects a
                        // CHANGED reference to an inactive buyer with a 400.
                        activeOnly
                        disabled={!canEditHeader}
                        required
                        error={!!errors.buyer}
                        helperText={errors.buyer}
                    />
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            data-help="merchandising.orderForm.field.orderNumber"
                            label="Order Number" value={form.orderNumber} onChange={setField('orderNumber')}
                            error={!!errors.orderNumber} helperText={errors.orderNumber}
                            inputProps={{ maxLength: MAX.orderNumber }} disabled={!canEditHeader} required fullWidth
                        />
                        <TextField
                            data-help="merchandising.orderForm.field.buyerPoNumber"
                            label="Buyer PO Number" value={form.buyerPoNumber} onChange={setField('buyerPoNumber')}
                            inputProps={{ maxLength: MAX.buyerPoNumber }} disabled={!canEditHeader} fullWidth
                        />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            data-help="merchandising.orderForm.field.orderDate"
                            label="Order Date" type="date" value={form.orderDate} onChange={setField('orderDate')}
                            error={!!errors.orderDate} helperText={errors.orderDate}
                            InputLabelProps={{ shrink: true }} disabled={!canEditHeader} required fullWidth
                        />
                        <TextField
                            data-help="merchandising.orderForm.field.shipDate"
                            label="Ship Date" type="date" value={form.shipDate} onChange={setField('shipDate')}
                            error={!!errors.shipDate} helperText={errors.shipDate}
                            InputLabelProps={{ shrink: true }} disabled={!canEditHeader} required fullWidth
                        />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            data-help="merchandising.orderForm.field.destinationCountry"
                            label="Destination Country" value={form.destinationCountry} onChange={setField('destinationCountry')}
                            inputProps={{ maxLength: MAX.destinationCountry }} disabled={!canEditHeader} fullWidth
                        />
                        <TextField
                            data-help="merchandising.orderForm.field.destinationPort"
                            label="Destination Port" value={form.destinationPort} onChange={setField('destinationPort')}
                            inputProps={{ maxLength: MAX.destinationPort }} disabled={!canEditHeader} fullWidth
                        />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            data-help="merchandising.orderForm.field.incoterm"
                            label="Incoterm" value={form.incoterm} onChange={setField('incoterm')}
                            inputProps={{ maxLength: MAX.incoterm }} disabled={!canEditHeader} fullWidth
                        />
                        <TextField
                            select data-help="merchandising.orderForm.field.currencyCode" label="Currency Code" value={form.currencyCode || ''}
                            onChange={setField('currencyCode')}
                            disabled={!canEditHeader} fullWidth
                        >
                            <MenuItem value="">USD (default)</MenuItem>
                            {['USD', 'EUR', 'GBP', 'BDT'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </TextField>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <Box sx={{ flex: { xs: 'none', sm: 1 }, minWidth: 0 }}>
                            <CommercialPartyAutocomplete
                                data-help="merchandising.orderForm.field.agent"
                                institutionId={institutionId}
                                partyTypes={['BUYING_AGENT']}
                                label="Buying agent (overrides buyer)"
                                value={form.agentPartyId}
                                onChange={party => setForm(prev => ({ ...prev, agentPartyId: party?.id ?? null }))}
                                disabled={!canEditHeader}
                                fullWidth
                            />
                            {!form.agentPartyId && (order?.effectiveAgentPartyName || form.buyer?.agentPartyName) && (
                                <Typography variant="caption" color="text.secondary">
                                    Inherited from buyer: {order?.effectiveAgentPartyName ?? form.buyer?.agentPartyName}
                                </Typography>
                            )}
                        </Box>
                        <TextField
                            data-help="merchandising.orderForm.field.agentCommission"
                            label="Agent commission %" type="number"
                            value={form.agentCommissionPercent}
                            onChange={e => setForm(prev => ({ ...prev, agentCommissionPercent: e.target.value }))}
                            error={!!errors.agentCommissionPercent}
                            helperText={
                                errors.agentCommissionPercent
                                    ?? (order?.agentCommissionAmount != null
                                        ? `Commission amount: ${order.agentCommissionAmount.toFixed(2)}`
                                        : undefined)
                            }
                            inputProps={{ step: 0.01, min: 0, max: 100 }}
                            disabled={!canEditHeader} fullWidth
                            sx={{ flex: { xs: 'none', sm: 1 }, minWidth: 0 }}
                        />
                    </Box>
                    <TextField
                        data-help="merchandising.orderForm.field.remarks"
                        label="Remarks" value={form.remarks} onChange={setField('remarks')}
                        inputProps={{ maxLength: MAX.remarks }} multiline minRows={2} disabled={!canEditHeader} fullWidth
                    />
                    {order && (
                        <Typography variant="body2" color="text.secondary">
                            Total quantity: {order.totalQuantity ?? 0} · Total value: {formatMoney(order.totalValue ?? 0, order.currencyCode)}
                        </Typography>
                    )}
                    {canEditHeader && (
                        <Box>
                            <Button data-help="merchandising.orderForm.save" variant="contained" onClick={handleSave} disabled={saving}>
                                {saving ? <CircularProgress size={20} /> : 'Save'}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Paper>

            {order && (
                <Paper variant="outlined" sx={{ p: 3 }} data-help-section="merchandising.orderForm.lines">
                    <BuyerOrderLinesEditor
                        lines={order.lines}
                        styleLookup={styleLookup}
                        buyerId={order.buyerId}
                        canManage={canManage}
                        readOnly={headerLocked}
                        readOnlyReason={
                            headerLocked
                                ? `Lines are read-only once the order leaves Draft (currently ${enumLabel(order.status)}).`
                                : undefined
                        }
                        saving={linesSaving}
                        savedTotal={order.totalValue ?? 0}
                        currencyCode={order.currencyCode}
                        onSave={handleSaveLines}
                    />
                </Paper>
            )}

            {order && (
                <>
                    <Divider sx={{ my: 3 }} />
                    <Paper variant="outlined" sx={{ p: 3, mb: 3 }} data-help-section="merchandising.orderForm.consumption">
                        <MaterialConsumptionEditor buyerOrderId={order.id} canManage={canManage} />
                    </Paper>
                    {hasPermission(PERMISSIONS.MERCH_REPORT_VIEW) && (
                        <>
                            <Divider sx={{ my: 3 }} />
                            <Paper variant="outlined" sx={{ p: 3, mb: 3 }} data-help-section="merchandising.orderForm.requirement">
                                <MaterialRequirementView buyerOrderId={order.id} />
                            </Paper>
                        </>
                    )}
                    <Divider sx={{ my: 3 }} />
                    <Paper variant="outlined" sx={{ p: 3 }} data-help-section="merchandising.orderForm.tna">
                        <TnaCalendarPanel buyerOrderId={order.id} canManage={canManage} />
                    </Paper>
                    {hasPermission(PERMISSIONS.QC_INSPECTION_VIEW) && (
                        <>
                            <Divider sx={{ my: 3 }} />
                            <Paper variant="outlined" sx={{ p: 3 }} data-help-section="merchandising.orderForm.qc">
                                <OrderQcPanel buyerOrderId={order.id} canManage={hasPermission(PERMISSIONS.QC_INSPECTION_MANAGE)} />
                            </Paper>
                        </>
                    )}
                </>
            )}

            {order && statusDialogOpen && (
                <BuyerOrderStatusDialog
                    open={statusDialogOpen}
                    order={order}
                    onClose={() => setStatusDialogOpen(false)}
                    onUpdated={handleStatusUpdated}
                />
            )}
            {order && amendDialogOpen && (
                <BuyerOrderAmendDialog
                    open={amendDialogOpen}
                    order={order}
                    onClose={() => setAmendDialogOpen(false)}
                    onUpdated={handleAmended}
                />
            )}
        </Box>
    );
}

export default BuyerOrderForm;
